import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  X as XIcon,
  Linkedin,
  Facebook,
  MessageCircle,
  Mail,
} from "lucide-react";

const APP_URL = typeof window !== "undefined" ? window.location.origin : "";

const targets = [
  {
    key: "linkedin",
    label: "LinkedIn",
    color: "#0A66C2",
    Icon: Linkedin,
    href: (url, text) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    key: "twitter",
    label: "X",
    color: "#0F1419",
    Icon: XIcon,
    href: (url, text) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(text)}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    color: "#1877F2",
    Icon: Facebook,
    href: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    color: "#25D366",
    Icon: MessageCircle,
    href: (url, text) =>
      `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
  },
];

export const ShareApp = ({ compact = false }) => {
  const { t } = useTranslation();
  const text = t("share.share_text") + " " + APP_URL;
  const emailSubject = t("share.email_subject");

  const openShare = (urlBuilder) => {
    const u = urlBuilder(APP_URL, t("share.share_text"));
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      toast.success(t("common.copied"));
    } catch {
      toast.error("Could not copy");
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent(
    emailSubject
  )}&body=${encodeURIComponent(text)}`;

  return (
    <div className={compact ? "" : "border border-border bg-secondary/40 p-6"}>
      {!compact && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-4 h-4 text-[#002FA7]" />
            <div className="sux-marker">{t("share.share_via")}</div>
          </div>
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {targets.map(({ key, label, color, Icon, href }) => (
          <button
            key={key}
            data-testid={`share-app-${key}`}
            onClick={() => openShare(href)}
            className="font-mono uppercase tracking-[0.15em] text-[11px] px-3 py-2 border border-border bg-card hover:text-white transition-colors flex items-center gap-2"
            style={{ "--c": color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = color)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
        <a
          href={mailto}
          data-testid="share-app-email"
          className="font-mono uppercase tracking-[0.15em] text-[11px] px-3 py-2 border border-border bg-card hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
        >
          <Mail className="w-3.5 h-3.5" />
          {t("share.email")}
        </a>
        <button
          onClick={copyLink}
          data-testid="share-app-copy"
          className="font-mono uppercase tracking-[0.15em] text-[11px] px-3 py-2 border border-[#FF2A00] text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
        >
          <Copy className="w-3.5 h-3.5" />
          {t("share.copy_link")}
        </button>
      </div>
    </div>
  );
};
